// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { AllProductsPageContext } from "@gcg/App/Pages/AllProducts";
import { parentCategoriesStyles } from "@gcg/App/Widgets/CategoryTilesDisplay/CategoryTiles.styles";
import { emptyGuid } from "@insite/client-framework/Common/StringHelpers";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCategoryState } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import loadCategories from "@insite/client-framework/Store/Data/Categories/Handlers/LoadCategories";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import Clickable from "@insite/mobius/Clickable";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem from "@insite/mobius/GridItem";
import LazyImage from "@insite/mobius/LazyImage/LazyImage";
import Link from "@insite/mobius/Link";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";

const mapStateToProps = (state: ApplicationState) => {
    const categoriesDataView = state.data.categories.parentCategoryIdToChildrenIds;
    const settingsCollection = getSettingsCollection(state);
    const categoriesCollection = [] as any;
    for (const [key, value] of Object.entries(categoriesDataView!)) {
        if (key === emptyGuid) {
            // eslint-disable-next-line array-callback-return
            categoriesDataView[key]!.map(categoryId => {
                const category = getCategoryState(state, categoryId);
                categoriesCollection.push(category);
            });
        }
    }

    return {
        settingsCollection,
        categoriesDataView,
        categoriesCollection,
    };
};

const mapDispatchToProps = {
    loadCategories,
};

type OwnProps = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const CategoryTilesDisplay: React.FC<OwnProps> = ({
    settingsCollection,
    categoriesDataView,
    categoriesCollection,
    loadCategories,
}) => {
    const [categories, setCategoies] = React.useState([] as any);
    const [customSettings, setCustomSettings] = React.useState([] as any);

    React.useEffect(() => {
        if (categoriesDataView.length || !categories.length) {
            // in case the state does not have the category data loaded yet
            loadCategories();
        }

        if (settingsCollection) {
            setCustomSettings(settingsCollection?.customSettings);
        }
    }, []);

    React.useEffect(() => {
        setCategoies(categoriesCollection);
    }, [categoriesDataView.length, categoriesCollection]);

    if (!categories || !categories.length) {
        return (
            <StyledWrapper {...parentCategoriesStyles.spinnerWrapper}>
                <LoadingSpinner {...parentCategoriesStyles.spinner} />
            </StyledWrapper>
        );
    }

    return (
        <>
            {categories && categories.length && (
                <GridContainer gap={20} {...parentCategoriesStyles.wrappingContainer}>
                    {categories.map((category: any) => (
                        <GridItem key={category.value.id} width={[6, 4, 3, 2, 2]}>
                            <Link
                                href={category.value.path}
                                {...parentCategoriesStyles.wrapperCategoryLink}
                                data-test-selector={`tst_parentCategoryUrl_${category.value.id}`}
                            >
                                <StyledWrapper {...parentCategoriesStyles.categoryImageWrapper}>
                                    <LazyImage
                                        data-test-selector={`tst_parentCategoryImagePath_${category.value.id}`}
                                        src={
                                            category.value.smallImagePath
                                                ? category.value.smallImagePath
                                                : customSettings?.mediumDefaultCategoryImage
                                        }
                                        altText={category.value.altText}
                                        {...parentCategoriesStyles.categoryImage}
                                    ></LazyImage>
                                </StyledWrapper>
                                <Link
                                    href={category.value.path}
                                    color="text.main"
                                    data-test-selector={`tst_parentCategoryDescription_${category.value.id}`}
                                    {...parentCategoriesStyles.categoryText}
                                >
                                    {category.value.shortDescription}
                                </Link>
                            </Link>
                        </GridItem>
                    ))}
                </GridContainer>
            )}
        </>
    );
};

const categoryTilesWidgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CategoryTilesDisplay),
    definition: {
        group: "Products",
        displayName: "Category Tiles Widget",
        allowedContexts: [AllProductsPageContext] as any,
    },
};

export default categoryTilesWidgetModule;
